import React from "react";
import { Box, Chip, Tab, Tabs } from "@mui/material";
import { getTranslation } from "common";

type TabValue = "todo" | "doing";

interface MobileTabBarProps {
  value: TabValue;
  onChange: (next: TabValue) => void;
  todoCount: number;
  doingCount: number;
  translations: Record<string, string>;
}

/**
 * Mobile tab bar with two tabs (Da Fare | In Corso) and a count chip per tab.
 * Used in the portrait layout of MyActivities.
 */
const MobileTabBar: React.FC<MobileTabBarProps> = ({
  value,
  onChange,
  todoCount,
  doingCount,
  translations,
}) => {
  const todoLabel = getTranslation(translations, "pm.label.process_status.todo");
  const doingLabel = getTranslation(translations, "pm.label.process_status.doing");

  const renderChip = (n: number, color: "primary" | "warning") => (
    <Chip
      label={n}
      size="small"
      sx={{
        ml: 0.75,
        height: 18,
        fontSize: 10,
        fontWeight: 700,
        bgcolor: color === "warning" ? "warning.light" : "primary.light",
        color: (t) => (t.palette.mode === "dark" ? "background.default" : "common.white"),
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
      <Tabs
        value={value}
        onChange={(_, next: TabValue) => onChange(next)}
        variant="fullWidth"
        TabIndicatorProps={{
          sx: {
            height: 2,
            bgcolor: value === "todo" ? "primary.main" : "warning.main",
          },
        }}
        sx={{
          minHeight: 44,
          "& .MuiTab-root": {
            minHeight: 44,
            py: 1,
            fontSize: 12,
            fontWeight: 700,
            textTransform: "none",
          },
        }}
      >
        <Tab
          value="todo"
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {todoLabel}
              {renderChip(todoCount, "primary")}
            </Box>
          }
          iconPosition="start"
        />
        <Tab
          value="doing"
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {doingLabel}
              {renderChip(doingCount, "warning")}
            </Box>
          }
          iconPosition="start"
        />
      </Tabs>
    </Box>
  );
};

export default MobileTabBar;